import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { getWebWidgetDemoConfig } from '../../connectors/services/webWidgetAdminService';
import { WebWidgetDemoConfig } from '../../connectors/types/webWidgetAdmin';
import { Spinner } from '../../../shared/components';
import { EmbeddedWebWidget } from './EmbeddedWebWidget';
import styles from './WebWidgetDemoPage.module.css';

const buildEmbedSnippet = (tenantCode: string, widgetKey: string): string =>
  `<EmbeddedWebWidget\n  tenantCode="${tenantCode}"\n  widgetKey="${widgetKey}"\n  embedMode="production"\n  defaultDisplayName="Website Visitor"\n/>`;

export const WebWidgetDemoPage: React.FC = () => {
  const [pollingIntervalSeconds, setPollingIntervalSeconds] = useState('15');
  const [demoConfig, setDemoConfig] = useState<WebWidgetDemoConfig | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);
  const [configLoading, setConfigLoading] = useState(true);
  const pageOrigin = typeof window !== 'undefined' ? window.location.origin : '';

  useEffect(() => {
    let active = true;

    const loadDemoConfig = async () => {
      setConfigLoading(true);
      setConfigError(null);

      try {
        const config = await getWebWidgetDemoConfig();
        if (!config.tenantCode || !config.widgetKey) {
          throw new Error('Tenant code or widget key is missing for the current tenant.');
        }

        if (active) {
          setDemoConfig(config);
        }
      } catch (error) {
        if (active) {
          setDemoConfig(null);
          setConfigError(error instanceof Error ? error.message : 'Unable to load widget demo configuration.');
        }
      } finally {
        if (active) {
          setConfigLoading(false);
        }
      }
    };

    void loadDemoConfig();

    return () => {
      active = false;
    };
  }, []);

  const pollingIntervalMs = useMemo(() => {
    const parsed = Number(pollingIntervalSeconds);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      return 0;
    }

    return parsed * 1000;
  }, [pollingIntervalSeconds]);

  const embedSnippet = useMemo(() => {
    if (!demoConfig) {
      return buildEmbedSnippet('YOUR_TENANT_CODE', 'YOUR_WIDGET_KEY');
    }

    return buildEmbedSnippet(demoConfig.tenantCode, demoConfig.widgetKey);
  }, [demoConfig]);

  return (
    <div className={styles.page}>
      <section className={styles.hero}>
        <div className={styles.heroCard}>
          <span className={styles.eyebrow}>Widget Lab</span>
          <h1 className={styles.title}>Hosted web widget, mounted inside the UI.</h1>
          <p className={styles.copy}>
            This route exists to validate the full frontend widget slice in a real page shell. It exercises the public-session client,
            reducer-backed session provider, and the embeddable chat surface against the backend widget endpoints.
          </p>
          <div className={styles.metaGrid}>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Tenant code</span>
              <span className={styles.metaValue}>
                {configLoading ? 'Loading…' : demoConfig?.tenantCode || '—'}
              </span>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Widget key</span>
              <span className={styles.metaValue}>
                {configLoading ? 'Loading…' : demoConfig?.widgetKey || '—'}
              </span>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Page origin</span>
              <span className={styles.metaValue}>{pageOrigin || '—'}</span>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Auth model</span>
              <span className={styles.metaValue}>Public session token</span>
            </div>
          </div>
          {configError ? (
            <p className={styles.configError}>
              {configError} Configure a tenant code in the central Tenants table and a WebChat widget in{' '}
              <Link to="/admin/connectors">Admin → Connectors</Link>.
            </p>
          ) : null}
        </div>

        <div className={styles.stack}>
          <aside className={styles.stackCard}>
            <h2 className={styles.stackTitle}>Embed surface</h2>
            <p className={styles.stackCopy}>
              Open the launcher in the lower-right corner. The widget will bootstrap, recover state, and post visitor messages through the public web widget API.
            </p>
            <p className={styles.stackCopy}>
              The widget&apos;s <strong>allowed origins</strong> must include this page&apos;s origin
              {pageOrigin ? (
                <> (<strong>{pageOrigin}</strong>)</>
              ) : null}
              {demoConfig ? (
                <>
                  . Add it in Admin → Connectors → WebChat for widget key <strong>{demoConfig.widgetKey}</strong>.
                </>
              ) : (
                '.'
              )}
            </p>
            <label className={styles.controlField}>
              <span className={styles.controlLabel}>Polling cadence</span>
              <select
                className={styles.controlInput}
                value={pollingIntervalSeconds}
                onChange={event => setPollingIntervalSeconds(event.target.value)}
              >
                <option value="5">5 seconds</option>
                <option value="10">10 seconds</option>
                <option value="15">15 seconds</option>
                <option value="30">30 seconds</option>
                <option value="0">Disabled</option>
              </select>
            </label>
          </aside>
          <aside className={styles.stackCard}>
            <h2 className={styles.stackTitle}>Usage sketch</h2>
            <p className={styles.stackCopy}>This is the minimal integration surface for a tenant website shell.</p>
            <pre className={styles.code}>{embedSnippet}</pre>
          </aside>
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.panel}>
          <h2 className={styles.panelTitle}>What this page validates</h2>
          <p className={styles.panelCopy}>
            It is intentionally simple: the point is not to look like the final tenant site, but to give the team a stable internal route for exercising widget bootstrap, session recovery, outbound send flow, paging, refresh, and reset behavior.
          </p>
          <ul className={styles.list}>
            <li className={styles.listItem}>
              <strong>Bootstrap path</strong>
              <span>Verifies widget key, origin-aware public session bootstrap, and appearance hydration.</span>
            </li>
            <li className={styles.listItem}>
              <strong>Session lifecycle</strong>
              <span>Exposes refresh and reset actions so token rotation and revoke behavior can be checked without a separate harness.</span>
            </li>
            <li className={styles.listItem}>
              <strong>Conversation handoff</strong>
              <span>Lets the team create a webchat-backed conversation and confirm the message lands in the internal conversations flow.</span>
            </li>
          </ul>
        </div>

        <div className={styles.panel}>
          <h2 className={styles.panelTitle}>Route</h2>
          <p className={styles.panelCopy}>
            Internal demo path: <strong>/web-widget-demo</strong>
          </p>
        </div>
      </section>

      {configLoading ? (
        <div className={styles.loadingState}>
          <Spinner text="Loading widget configuration…" />
        </div>
      ) : null}

      {!configLoading && demoConfig ? (
        <EmbeddedWebWidget
          tenantCode={demoConfig.tenantCode}
          widgetKey={demoConfig.widgetKey}
          embedMode="production"
          defaultDisplayName="Website Visitor"
          pollingIntervalMs={pollingIntervalMs}
        />
      ) : null}
    </div>
  );
};

export default WebWidgetDemoPage;
